/**
 * Chainlit-quality markdown renderer for chat messages.
 *
 * Features:
 *   - GitHub-Flavored Markdown (tables, task lists, strikethrough, autolinks) via remark-gfm
 *   - Math (inline `$...$`, block `$$...$$`) via remark-math + rehype-katex
 *   - Syntax-highlighted code blocks (PrismLight + oneLight theme), hover-copy button
 *   - Inline code, styled tables, callout-style blockquotes, task-list checkboxes,
 *     external links open in new tab, lazy-loaded images.
 *
 * Used by `renderMessage.jsx` (default agent-text branch) and DesignView's
 * live-streaming bubble so streaming and finalized text render identically.
 *
 * Bundle size: uses the PrismLight build with only the languages we actually
 * expect. Adding new languages is a one-liner registration below.
 *
 * Performance: Wrapped in React.memo to prevent re-parsing markdown on every
 * SSE chunk. Settled messages skip re-render entirely; only the active
 * streaming message (growing content) re-parses each chunk.
 */
import React, { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Languages — keep this list small for bundle size. Add more as needed.
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown';

SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('js', javascript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('ts', typescript);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('py', python);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('sh', bash);
SyntaxHighlighter.registerLanguage('shell', bash);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('tsx', tsx);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('yml', yaml);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('md', markdown);

// Hoist plugin arrays to module scope so their identities are stable across
// renders. Inline arrays defeat React.memo memoization.
const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeKatex];

// ── Code block with copy button ──────────────────────────────────────
function CodeBlock({ language, value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // very old browsers — best-effort fallback
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // swallow — clipboard can throw on cross-origin / no-permission
    }
  }, [value]);

  return (
    <div className="rich-code-block">
      {language && <span className="rich-code-lang">{language}</span>}
      <button
        type="button"
        className="rich-code-copy"
        onClick={handleCopy}
        aria-label="Copy code"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneLight}
        PreTag="div"
        customStyle={{
          margin: 0,
          padding: '12px 14px',
          borderRadius: 8,
          fontSize: '12.5px',
          background: 'var(--surface-2, #FAFAFB)',
          border: '1px solid var(--border, #E1E1E5)',
        }}
        codeTagProps={{
          style: {
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          },
        }}
      >
        {value.replace(/\n$/, '')}
      </SyntaxHighlighter>
    </div>
  );
}

// ── react-markdown components map ────────────────────────────────────
// Hoist to module scope so the object identity is stable across renders.
// Inline object creation defeats React.memo memoization.
const COMPONENTS = {
  code({ inline, className, children, ...props }) {
    const match = /language-([\w-]+)/.exec(className || '');
    const value = String(children ?? '');

    if (!inline && match) {
      return <CodeBlock language={match[1]} value={value} />;
    }
    // some renderers don't pass `inline`; treat multiline content as a block.
    if (!inline && value.includes('\n')) {
      return <CodeBlock language={match ? match[1] : ''} value={value} />;
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },

  a({ children, href, ...props }) {
    const isExternal = href && /^https?:\/\//i.test(href);
    return (
      <a
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    );
  },

  table({ children, ...props }) {
    return (
      <div className="rich-table-wrap">
        <table {...props}>{children}</table>
      </div>
    );
  },

  ul({ className, children, ...props }) {
    // remark-gfm marks task lists with `contains-task-list` on the <ul>
    const isTaskList =
      className && /contains-task-list/.test(className);
    const cls = [className, isTaskList ? 'task-list' : '']
      .filter(Boolean)
      .join(' ');
    return (
      <ul className={cls || undefined} {...props}>
        {children}
      </ul>
    );
  },

  li({ className, children, ...props }) {
    const isTaskItem = className && /task-list-item/.test(className);
    const cls = [className, isTaskItem ? 'task-item' : '']
      .filter(Boolean)
      .join(' ');
    return (
      <li className={cls || undefined} {...props}>
        {children}
      </li>
    );
  },

  img({ alt, src, ...props }) {
    return (
      <img
        src={src}
        alt={alt || ''}
        loading="lazy"
        className="rich-img"
        {...props}
      />
    );
  },

  blockquote({ children, ...props }) {
    return <blockquote {...props}>{children}</blockquote>;
  },
};

// Wrap in React.memo so settled messages (whose content hasn't changed) skip
// the markdown parse entirely. Only the currently-streaming message (growing
// content) re-renders each chunk. Custom equality: re-render only when content
// changes (all other props are stable module-scope constants).
// The model sometimes wraps its own markdown output in a ```markdown fence
// (same defect class as the canvas converter's unwrap in the backend's
// _md_to_blocks.py) — rendered literally that puts raw pipe-tables and ##
// headings in a code box. Splice such fence bodies back in as markdown.
// Real code fences (json/pql/bash/…) and unlabeled fences are untouched.
function unwrapMarkdownFences(md) {
  if (!md || md.indexOf('```') === -1) return md;
  return md.replace(/^```(?:markdown|md)[ \t]*\n([\s\S]*?)\n```[ \t]*$/gm, '$1');
}

function RichMarkdownComponent({ content }) {
  // useMemo the ReactMarkdown element on content so we don't re-parse identical
  // markdown when other props (which are stable) might have changed.
  const rendered = useMemo(
    () => (
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={COMPONENTS}
      >
        {unwrapMarkdownFences(content || '')}
      </ReactMarkdown>
    ),
    [content],
  );

  return rendered;
}

const RichMarkdown = React.memo(RichMarkdownComponent, (prevProps, nextProps) => prevProps.content === nextProps.content);

export default RichMarkdown;
